import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  AppBar, 
  Toolbar, 
  Button, 
  Box, 
  Menu, 
  MenuItem, 
  IconButton, 
  Typography,
  Avatar,
  Divider,
  Chip
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import PersonIcon from '@mui/icons-material/Person';
import DashboardIcon from '@mui/icons-material/Dashboard';
import StorageIcon from '@mui/icons-material/Storage';
import BusinessIcon from '@mui/icons-material/Business';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useAuth } from '../../contexts/AuthContext';
import { useGuardedLinkClick } from '../../utils/canvasEditGuard';
import { styled } from '@mui/material/styles';
import { Link as MuiLink } from '@mui/material';
import { motion } from 'framer-motion';

const GradientAppBar = styled(AppBar)(({ theme }) => ({
  background: theme.palette.background.default,
  borderBottom: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  height: '64px',
  position: 'sticky',
  top: 0,
  zIndex: theme.zIndex.drawer + 1,
}));

const Logo = styled('img')({
  height: '28px',
  marginRight: '10px',
  filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2))',
});

const HeaderButton = styled(Button)(({ theme }) => ({
  marginLeft: theme.spacing(1),
  padding: '6px 12px',
  borderRadius: '4px',
  fontSize: '0.9rem',
  fontWeight: 500,
  textTransform: 'none',
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: 'rgba(78, 133, 235, 0.08)',
    transform: 'none',
    transition: 'all 0.2s',
  },
  '&.active': {
    background: 'rgba(78, 133, 235, 0.12)',
    color: theme.palette.primary.main,
  },
}));

const AnimatedHeaderButton = motion(HeaderButton);

const NavIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.primary,
  margin: theme.spacing(0, 0.5),
  padding: theme.spacing(1),
  '&:hover': {
    backgroundColor: 'rgba(78, 133, 235, 0.08)',
  },
}));

const EmulationChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(255, 152, 0, 0.15)',
  color: theme.palette.warning.dark,
  fontWeight: 500,
  marginRight: theme.spacing(2),
  '& .MuiChip-icon': {
    color: theme.palette.warning.dark,
  }
}));

function Header() {
  const [anchorEl, setAnchorEl] = useState(null);
  const { tokenOrganisation, companyName } = useAuth();
  const guardNav = useGuardedLinkClick();

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Check if the user is emulating an organization
  const isEmulating = tokenOrganisation === 'all' && companyName;

  return (
    <GradientAppBar position="sticky">
      <Toolbar 
        sx={{ 
          justifyContent: 'space-between', 
          height: '100%',
          alignItems: 'center',
          padding: { xs: '0 16px', sm: '0 24px' },
          pl: { lg: '15%' },
        }}
      >
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            position: { lg: 'absolute' }, 
            left: { lg: '1%' },
            width: { lg: '10%' },
            justifyContent: 'center',
          }}
        >
          <MuiLink href="https://bimanalytics.ai" underline="none" sx={{ display: 'flex', alignItems: 'center' }}>
            <Logo src={`${window.location.origin}/logo-white.png`} alt="BIM Logo" />
            <Typography 
              variant="h6"
              sx={{ 
                display: { xs: 'none', sm: 'flex' },
                fontWeight: 500,
                color: 'white',
                letterSpacing: '0.02em',
                fontSize: '2rem',
              }}
            >
              DECODE
            </Typography>
          </MuiLink>
        </Box>
        <Box sx={{ 
          display: { xs: 'none', md: 'flex' },
          height: '100%',
          alignItems: 'center',
          ml: 'auto', // Push to the right side
        }}>
          {isEmulating && (
            <EmulationChip
              icon={<BusinessIcon />}
              label={`Viewing as: ${companyName}`}
              size="small"
            />
          )}
          <AnimatedHeaderButton
            color="inherit"
            component={Link}
            to="/agent"
            onClick={guardNav('/agent')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            startIcon={<AutoAwesomeIcon />}
          >
            Agent
          </AnimatedHeaderButton>
          <AnimatedHeaderButton
            color="inherit"
            component={Link}
            to="/"
            onClick={guardNav('/')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            startIcon={<DashboardIcon />}
          >
            Dashboard
          </AnimatedHeaderButton>
          <AnimatedHeaderButton
            color="inherit"
            component={Link}
            to="/data"
            onClick={guardNav('/data')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            startIcon={<StorageIcon />}
          >
            Data
          </AnimatedHeaderButton>
        </Box>
        <NavIconButton
          size="large"
          edge="start"
          color="inherit"
          aria-label="menu"
          sx={{ 
            display: { xs: 'block', md: 'none' },
            alignSelf: 'center'
          }}
          onClick={handleMenu}
        >
          <MenuIcon />
        </NavIconButton>
        <Menu
          id="menu-appbar"
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          PaperProps={{
            elevation: 3,
            sx: {
              backgroundColor: 'background.paper',
              borderRadius: '8px',
              mt: 1,
              minWidth: '200px',
              overflow: 'hidden',
              '& .MuiMenuItem-root': {
                py: 1.2,
                px: 2,
                '&:hover': {
                  backgroundColor: 'rgba(78, 133, 235, 0.08)'
                }
              }
            }
          }}
        >
          {isEmulating && (
            <>
              <MenuItem disabled>
                <BusinessIcon sx={{ mr: 1.5, color: 'warning.dark' }} />
                <Typography variant="body2" color="warning.dark">
                  Viewing as: {companyName}
                </Typography>
              </MenuItem>
              <Divider />
            </>
          )}
          <MenuItem onClick={(e) => { handleClose(); guardNav('/agent')(e); }} component={Link} to="/agent">
            <AutoAwesomeIcon sx={{ mr: 1.5, color: 'primary.main' }} /> Agent
          </MenuItem>
          <MenuItem onClick={(e) => { handleClose(); guardNav('/')(e); }} component={Link} to="/">
            <DashboardIcon sx={{ mr: 1.5, color: 'primary.main' }} /> Dashboard
          </MenuItem>
          <MenuItem onClick={(e) => { handleClose(); guardNav('/data')(e); }} component={Link} to="/data">
            <StorageIcon sx={{ mr: 1.5, color: 'primary.main' }} /> Data
          </MenuItem>
        </Menu>
      </Toolbar>
    </GradientAppBar>
  );
}

export default Header;